// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";

import { clearAuthToken, getAuthToken } from "../auth";
import { invokeAuthFailureHandler } from "./access-token-bridge";
import { AUTH_STATE, authState } from "./policies/auth";
import { isTerminalRefreshFailure, refreshMonitorToken } from "./refresh-token";

/**
 * Apollo error link: owns the 401 verdict.
 *
 * This is the *only* place that decides a mid-session auth failure has
 * become a sign-out. CHECK_AUTH's `onError` used to make the same call
 * independently and the two raced, which is half of MONITOR-135: a
 * recoverable blip got adjudicated as a terminal logout by whichever
 * handler ran first.
 *
 * The recovery path publishes its progress into the auth state machine
 * so the UI can stay put. A 401 on a live session moves LOGGED_IN ->
 * REFRESHING (tree stays mounted, URL unchanged), and only a *terminal*
 * refresh failure moves REFRESHING -> LOGGED_OUT.
 *
 * Lives outside index.js so it can be tested without booting the app.
 */

// Codes the gateway / Apollo plugin layer uses to signal "your token
// isn't trusted any more". Treat any of these the same way as a raw
// HTTP 401: try a single-flight refresh, and only fall back to forcing
// a re-sign-in if the refresh itself fails.
const AUTH_FAILURE_CODES = new Set([
  "UNAUTHENTICATED",
  "INVALID_TOKEN",
  "AUTH_REQUIRED",
  "E_TOKEN_FAILED",
]);

export const isAuthFailureGraphQLError = (error) => {
  if (!error || !error.extensions) return false;
  const http = error.extensions.http;
  if (http === 401 || http === 402) return true;
  return AUTH_FAILURE_CODES.has(error.extensions.code);
};

export const isAuthFailureNetworkError = (networkError) => {
  if (!networkError) return false;
  if (networkError.statusCode === 401 || networkError.statusCode === 403) return true;
  const errors = networkError.result?.errors;
  if (Array.isArray(errors)) {
    return errors.some(isAuthFailureGraphQLError);
  }
  return false;
};

// Backoff between refresh attempts for *recoverable* failures only
// (network error, 5xx, a response that carried no JWT). A terminal
// failure short-circuits the loop: retrying a rejected refresh cookie
// just burns a round trip and risks tripping replay detection.
//
// Exported so tests can shorten it rather than sit through real waits.
export const REFRESH_RETRY_DELAYS_MS = [250, 1000];

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Refresh with retries. Resolves with the fresh JWT, or rejects with
 * the last RefreshError seen. Terminal failures reject immediately.
 */
export const refreshWithRetry = async (client, delays = REFRESH_RETRY_DELAYS_MS) => {
  let lastError;
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await refreshMonitorToken(client);
    } catch (error) {
      lastError = error;
      if (isTerminalRefreshFailure(error)) throw error;
      const delay = delays[attempt];
      if (delay === undefined) throw lastError;
      await sleep(delay);
    }
  }
};

/**
 * Decide whether a 401 is worth recovering from, and advertise the
 * attempt in the state machine when it is legal to do so.
 *
 * Returns false to mean "do not attempt recovery at all".
 *
 * Three cases:
 *
 *   LOGGED_IN / REFRESHING — a live session. Move to REFRESHING so
 *     WithAuth keeps the tree mounted for the duration.
 *   LOGGED_OUT / LOGGING_OUT — the session is already over. A straggler
 *     401 from a query that was still in flight must not resurrect the
 *     authenticated tree over the top of the login page.
 *   WAITING — boot has not adjudicated yet. Still worth attempting the
 *     refresh (it can rescue a stale token on a reload), but do NOT
 *     move to REFRESHING: that would render the authenticated tree
 *     before anyone has established there is a user. Staying in WAITING
 *     keeps the Loading fallback up, and the replayed CheckAuth
 *     resolves the state on the way back.
 */
const beginRefreshing = () => {
  const current = authState();
  if (current === AUTH_STATE.LOGGED_OUT || current === AUTH_STATE.LOGGING_OUT) {
    return false;
  }
  if (current === AUTH_STATE.LOGGED_IN || current === AUTH_STATE.REFRESHING) {
    authState(AUTH_STATE.REFRESHING);
  }
  return true;
};

const finishRefreshing = () => {
  // Only settle back to LOGGED_IN if nothing else has moved the machine
  // on in the meantime (a deliberate logout, a cross-tab signout).
  if (authState() === AUTH_STATE.REFRESHING) {
    authState(AUTH_STATE.LOGGED_IN);
  }
};

const failRefreshing = (terminal) => {
  clearAuthToken();
  // The handler registered by auth-context owns the sign-out: it flips
  // to LOGGED_OUT, captures the return target, fans the signout out to
  // peer tabs, and tears down the Logto SDK session when (and only
  // when) the failure was terminal.
  invokeAuthFailureHandler({ terminal });
};

/**
 * @param getClient  Returns the ApolloClient to fire the refresh
 *   mutation through. It is a thunk because the client is constructed
 *   *from* this link, so it does not exist yet at call time.
 *
 *   This is not incidental plumbing. The previous implementation read
 *   `operation.getContext().client`, which Apollo Client does not
 *   populate (verified against 3.14: `QueryManager.prepareContext`
 *   merges only defaultContext and clientAwareness). So every
 *   mid-session 401 called `refreshMonitorToken(undefined)`, threw
 *   `Cannot read properties of undefined (reading 'mutate')` inside
 *   the helper, and landed in the catch that signs the user out. The
 *   recovery path has never once run. That is the mechanism behind
 *   MONITOR-135's "flash of the login page": the 401 signed the user
 *   out instantly, and the AuthProvider bootstrap effect — which does
 *   hold a real client — then silently refreshed and signed them back
 *   in a moment later.
 */
export const createErrorLink = ({ getClient, retryDelays = REFRESH_RETRY_DELAYS_MS } = {}) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    // Credential-minting operations are not recovered by firing another
    // refresh at them; each is handled on its own terms below.
    const opName = operation?.operationName;
    const isRefreshOp = opName === "RefreshMonitorToken";
    const isExchangeOp = opName === "ExchangeLogtoToken";

    let authFailure = false;
    if (graphQLErrors && graphQLErrors.some(isAuthFailureGraphQLError)) authFailure = true;
    if (isAuthFailureNetworkError(networkError)) authFailure = true;

    if (!authFailure) return;

    if (isRefreshOp) {
      // A 401 on the refresh mutation itself. Do NOT adjudicate it
      // here: `refreshWithRetry` below owns this verdict, and it holds
      // both the retry budget and the distinction between a rejected
      // *cookie* (terminal) and a rejected *bearer* (not a verdict on
      // the session at all).
      //
      // Signing out from here pre-empted both. It ran on the first
      // failure, before any retry, and it always said `terminal: true`,
      // so a gateway 401 on the refresh op cost the user their session
      // outright, and even a retry that went on to succeed
      // could not rescue them: `finishRefreshing` only restores
      // LOGGED_IN from REFRESHING, and this had already moved the
      // machine to LOGGED_OUT. That is MONITOR-182's second defect.
      // The outer `.catch` reports the failure once, after the helper
      // has finished with it.
      return;
    }

    if (isExchangeOp) {
      // The exchange has no cookie to fall back on and no retry loop:
      // Callback.js drives it once, at sign-in. Nothing to wait for.
      failRefreshing(true);
      return;
    }

    // Publish the in-flight refresh BEFORE awaiting it. This is the
    // line that keeps the user's page on screen: WithAuth treats
    // REFRESHING as signed in, so the authenticated subtree stays
    // mounted and the URL is untouched for the whole round trip.
    if (!beginRefreshing()) return;

    const client = getClient?.();
    if (!client) {
      // Misconfiguration, not an auth failure. Say so loudly: silently
      // falling through to a sign-out here is precisely the bug this
      // parameter exists to prevent, and it is invisible from the UI.
      // biome-ignore lint/suspicious/noConsole: deliberate diagnostic for unhandled GraphQL transport errors
      console.error(
        "errorLink has no Apollo client; cannot refresh. Check createErrorLink({ getClient }).",
      );
      failRefreshing(true);
      return;
    }

    return new Observable((observer) => {
      let subscriber;
      let cancelled = false;

      refreshWithRetry(client, retryDelays)
        .then(() => {
          if (cancelled) return;
          finishRefreshing();
          // Replay the original operation against the fresh token.
          // Later operations pick it up from authLink as normal.
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              authorization: `Bearer ${getAuthToken()}`,
            },
          }));
          subscriber = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((error) => {
          if (cancelled) return;
          // Terminal, or recoverable but out of retries. Either way the
          // session is over; `terminal` decides whether we also tear
          // down the Logto SDK session.
          failRefreshing(isTerminalRefreshFailure(error));
          observer.error(new Error("REFRESH_FAILED"));
        });

      return () => {
        cancelled = true;
        if (subscriber) subscriber.unsubscribe();
      };
    });
  });

export default createErrorLink;
